<template>
  <div
    :class="[
      'rounded-lg p-4',
      variantClasses
    ]"
    role="alert"
  >
    <div class="flex">
      <!-- Icon -->
      <div v-if="$slots.icon" class="flex-shrink-0">
        <slot name="icon" />
      </div>

      <div class="flex-1" :class="{ 'ml-3': $slots.icon }">
        <!-- Title -->
        <h3 v-if="$slots.title" :class="titleClasses">
          <slot name="title" />
        </h3>

        <!-- Content -->
        <div :class="[contentClasses, { 'mt-2': $slots.title }]">
          <slot />
        </div>
      </div>

      <!-- Dismiss Button -->
      <div v-if="dismissible" class="ml-auto pl-3">
        <button
          type="button"
          @click="dismiss"
          :class="dismissButtonClasses"
        >
          <span class="sr-only">Dismiss</span>
          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'success', 'warning', 'danger', 'secondary'].includes(value),
  },
  dismissible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['dismiss']);

/**
 * Get variant-specific classes for the alert container
 */
const variantClasses = computed(() => {
  const classes = {
    info: 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    success: 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800',
    warning: 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
    danger: 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800',
    secondary: 'bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700',
  };
  return classes[props.variant];
});

/**
 * Get variant-specific classes for the title
 */
const titleClasses = computed(() => {
  const classes = {
    info: 'text-blue-800 dark:text-blue-200 font-medium text-sm',
    success: 'text-green-800 dark:text-green-200 font-medium text-sm',
    warning: 'text-yellow-800 dark:text-yellow-200 font-medium text-sm',
    danger: 'text-red-800 dark:text-red-200 font-medium text-sm',
    secondary: 'text-gray-800 dark:text-gray-200 font-medium text-sm',
  };
  return classes[props.variant];
});

/**
 * Get variant-specific classes for the content
 */
const contentClasses = computed(() => {
  const classes = {
    info: 'text-blue-700 dark:text-blue-300 text-sm',
    success: 'text-green-700 dark:text-green-300 text-sm',
    warning: 'text-yellow-700 dark:text-yellow-300 text-sm',
    danger: 'text-red-700 dark:text-red-300 text-sm',
    secondary: 'text-gray-700 dark:text-gray-300 text-sm',
  };
  return classes[props.variant];
});

/**
 * Get variant-specific classes for the dismiss button
 */
const dismissButtonClasses = computed(() => {
  const classes = {
    info: 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300',
    success: 'text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300',
    warning: 'text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300',
    danger: 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300',
    secondary: 'text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300',
  };
  return `inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${classes[props.variant]}`;
});

/**
 * Dismiss the alert
 */
const dismiss = () => {
  emit('dismiss');
};
</script>
